
import { Component, Vue } from 'vue-property-decorator';
import BarChart from '@/services/QA/statistics/charts/BarChart';
import { StatisticsRepositoryInterface, StatisticsResponseInterface } from '@/services/QA/statistics/interfaces';
import MainDailyLineChart from '@/services/QA/statistics/charts/MainDailyLineChart';
import DailyScoreLineChart from '@/services/QA/statistics/charts/DailyScoreLineChart';
import {
  SelectUserInterface,
  UserRepoInterface,
} from '@/services/Users/Interfaces';
import DepartmentRepositoryInterface, { DepartmentInterface } from '@/services/Departments/Interfaces';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import TagRepositoryInterface from '@/services/QA/tag/interfaces';


@Component({
  inject: [
    'statisticRepository',
    'userRepository',
    'departmentRepository',
    'profileRepository',
  ],
  components: {
    BarChart,
    LineChart: MainDailyLineChart,
    ScoreLineChart: DailyScoreLineChart,
  },
})
export default class MainStatistic extends Vue {
  private statisticRepository!: StatisticsRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private departmentRepository!: DepartmentRepositoryInterface;
  private profileRepository!: ProfileRepositoryInterface;
  private chartData!: StatisticsResponseInterface;
  private loading: boolean = true;
  private currentDate: Date = new Date();
  private fromDate: string = '';
  private toDate: string = '';
  private menuFrom: boolean = false;
  private menuTo: boolean = false;
  private preset: number = -1;
  private selectedCharts: string[] = ['main', 'daily-main'];
  private users: SelectUserInterface[] = [];
  private selectedUser: string = '';
  private selectedDepartment: string = '';
  private departments: DepartmentInterface[];

  private presets: Array<{ label: string, value: number }> = [
    { label: 'Last 3 Months', value: 3 },
    { label: 'Last 6 Months', value: 6 },
    { label: 'Last 1 Year', value: 12 },
    { label: 'All Time', value: 0 },
  ];

  private statistics: Array<{ label: string, value: string }> = [
    { label: 'Main Statistic', value: 'main' },
    { label: 'Daily Main Statistic', value: 'daily-main' },
    { label: 'Daily Average Chat Scores', value: 'daily-chat-score' },
    { label: 'Daily Average Ticket Scores', value: 'daily-ticket-score' },
    { label: 'Average Chat Scores', value: 'average-chat-score' },
    { label: 'Average Ticket Scores', value: 'average-ticket-score' },
  ];

  public async created(): Promise<void> {
    this.departments = await this.departmentRepository.getAllDepartments();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    const fromDate = this.formatDate(new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1));
    const toDate = this.formatDate(new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0));
    this.fromDate = fromDate;
    this.toDate = toDate;
    const chartData = await this.statisticRepository.getMainStatistics(fromDate, toDate);
    this.sortData(chartData);
    this.chartData = chartData;
    this.loading = false;
  }

  private async fetchData(): Promise<void> {
    this.preset = -1;
    if (!this.toDate) {
      this.toDate = this.formatDate(this.currentDate);
    }

    if (!this.fromDate) {
      this.fromDate = this.formatDate(this.currentDate);
    }

    if (this.selectedUser) {
      await this.getUserStatistic();
      return;
    }

    const chartData = await this.statisticRepository.getMainStatistics(this.fromDate, this.toDate);
    this.sortData(chartData);
    this.chartData = chartData;
    this.$forceUpdate();
  }

  private async loadPresetData(): Promise<void> {
    const pastDate = new Date();
    pastDate.setMonth(-this.preset);
    const fromDate = this.formatDate(new Date(pastDate.getFullYear(), pastDate.getUTCMonth(), pastDate.getDate()));
    const currentDate = this.formatDate(this.currentDate);
    let chartData: StatisticsResponseInterface;
    if (this.preset === 0) {
      chartData = await this.statisticRepository.getMainStatistics('', '');
    } else {
      chartData = await this.statisticRepository.getMainStatistics(fromDate, currentDate);
    }
    this.sortData(chartData);
    this.chartData = chartData;

    this.fromDate = '';
    this.toDate = '';
    this.$forceUpdate();
  }

  private formatDate(date: Date): string {
    let month = String(date.getMonth() + 1);
    if (Number(month) < 10) {
      month = '0' + month;
    }
    let day = String(date.getDate());
    if (Number(day) < 10) {
      day = '0' + day;
    }
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  private async updateUsers(): Promise<void> {
    if (!this.selectedDepartment) {
      return;
    }

    this.users = [];
    const users = await this.userRepository.getUsersByDepartment(this.selectedDepartment);
    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds([
      ...users.map((user) => String(user.id)),
    ]);

    profiles.forEach((profile) => {
      this.users.push({
        id: profile.userId,
        name: `${profile.firstName} ${profile.lastName}`,
      });
    });
  }

  private async getUserStatistic(): Promise<void> {
    if (!this.selectedUser) {
      return;
    }

    const chartData = await this.statisticRepository.getAgentStatistic(this.selectedUser, this.fromDate, this.toDate);
    this.sortData(chartData);
    this.chartData = chartData;
    this.$forceUpdate();
  }

  private chartIsSelected(chart: string): boolean {
    return this.selectedCharts.indexOf(chart) !== -1;
  }

  private sortData(chartData: StatisticsResponseInterface): void {
    if (chartData.dailyMainStatistic.length > 0) {
      chartData.dailyMainStatistic = chartData.dailyMainStatistic
        .sort((a, b) => a.date.localeCompare(b.date));
    }

    if (chartData.dailyScoreStatistic.chatScores.length > 0) {
      chartData.dailyScoreStatistic.chatScores = chartData.dailyScoreStatistic.chatScores
        .sort((a, b) => a.date.localeCompare(b.date));
    }

    if (chartData.dailyScoreStatistic.ticketScores.length > 0) {
      chartData.dailyScoreStatistic.ticketScores = chartData.dailyScoreStatistic.ticketScores
        .sort((a, b) => a.date.localeCompare(b.date));
    }
  }

  private async updateTime(date: string): Promise<void> {
    this.loading = true;
    let chartData: StatisticsResponseInterface;
    const dateObj = new Date(date);
    const fromDate = this.formatDate(dateObj);
    const toDate = this.formatDate(dateObj);
    this.fromDate = fromDate;
    this.toDate = toDate;

    if (this.selectedUser) {
      chartData = await this.statisticRepository.getAgentStatistic(this.selectedUser, this.fromDate, this.toDate);
      this.sortData(chartData);
      this.chartData = chartData;
      this.loading = false;
      return;
    }

    chartData = await this.statisticRepository.getMainStatistics(fromDate, toDate);
    this.sortData(chartData);
    this.chartData = chartData;
    this.loading = false;
  }
}
